import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { api } from "../lib/api";

const badgeBase = "flex h-7 min-w-[44px] items-center justify-center rounded bg-white px-2 text-[9px] font-black uppercase tracking-tight";

const PaymentIcons = () => (
  <div className="flex flex-wrap items-center justify-center gap-1.5 md:justify-end" data-testid="footer-payment-icons">
    <span role="img" className={`${badgeBase} text-[#1a1f71]`} data-testid="pay-visa" aria-label="Visa" title="Visa">Visa</span>
    <span role="img" className={`${badgeBase} gap-0.5`} data-testid="pay-mastercard" aria-label="Mastercard" title="Mastercard">
      <svg viewBox="0 0 32 20" className="h-4 w-auto" aria-hidden="true">
        <circle cx="12" cy="10" r="8" fill="#eb001b" />
        <circle cx="20" cy="10" r="8" fill="#f79e1b" fillOpacity="0.9" />
      </svg>
    </span>
    <span role="img" className={badgeBase} data-testid="pay-amex" aria-label="American Express" title="American Express">
      <span className="text-[#006fcf]" aria-hidden="true">Amex</span>
    </span>
  </div>
);

const SocialIcons = ({ social }) => {
  const items = [
    ["Facebook", social.facebook, Facebook, "footer-social-facebook"],
    ["Instagram", social.instagram, Instagram, "footer-social-instagram"],
    ["Twitter", social.twitter, Twitter, "footer-social-twitter"],
    ["YouTube", social.youtube, Youtube, "footer-social-youtube"],
  ];

  return (
    <div className="flex items-center gap-3" data-testid="footer-social-icons">
      {items.map(([name, url, Icon, tid]) => {
        const disabled = !url;
        return (
          <a
            key={name}
            href={url || "#"}
            target={url ? "_blank" : undefined}
            rel={url ? "noreferrer" : undefined}
            aria-label={name}
            title={disabled ? `${name} — add URL in admin` : name}
            data-testid={tid}
            onClick={disabled ? (e) => e.preventDefault() : undefined}
            className={`flex h-10 w-10 items-center justify-center border-2 border-white/40 text-white transition-colors duration-200 hover:border-white hover:bg-white hover:text-black ${disabled ? "opacity-40 hover:bg-transparent hover:text-white" : ""}`}
          >
            <Icon size={16} />
          </a>
        );
      })}
    </div>
  );
};

export const Footer = () => {
  const [social, setSocial] = useState({});
  const [f, setF] = useState({
    about_text: "",
    phone_ind: "",
    phone_usa: "",
    email: "",
    address: "",
    copyright: "",
  });
  useEffect(() => {
    api.get("/site/social").then((r) => setSocial(r.data || {})).catch(() => {});
    api.get("/site/footer").then((r) => setF((prev) => ({ ...prev, ...(r.data || {}) }))).catch(() => {});
  }, []);

  const telHref = (num) => `tel:${(num || "").replace(/[^+\d]/g, "")}`;
  const copyrightRendered = (f.copyright || "").replace("{year}", String(new Date().getFullYear()));

  return (
    <footer className="border-t-2 border-black bg-black" data-testid="main-footer">
      <div className="mx-auto grid max-w-7xl gap-x-16 gap-y-10 px-4 py-16 sm:px-8 md:grid-cols-[1.5fr_1fr_1fr_1fr]">
        <div>
          <p className="flex items-center font-display text-xl font-black tracking-tighter text-white">
            VVS<span className="ml-1 bg-white px-1.5 py-0.5 text-black">LAB</span>
            <span className="ml-2 text-[10px] font-normal uppercase tracking-[0.3em] text-zinc-400">jewelry</span>
          </p>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-zinc-400" data-testid="footer-about-text">
            {f.about_text}
          </p>
          <div className="mt-6 space-y-2 text-sm text-zinc-300" data-testid="footer-contact">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Contact</p>
            {f.phone_ind && (
              <p>
                <span className="text-zinc-500">IND</span>{" "}
                <a href={telHref(f.phone_ind)} className="transition-colors hover:text-white" data-testid="footer-phone-ind">{f.phone_ind}</a>
              </p>
            )}
            {f.phone_usa && (
              <p>
                <span className="text-zinc-500">USA</span>{" "}
                <a href={telHref(f.phone_usa)} className="transition-colors hover:text-white" data-testid="footer-phone-usa">{f.phone_usa}</a>
              </p>
            )}
            {f.email && (
              <p>
                <a href={`mailto:${f.email}`} className="transition-colors hover:text-white" data-testid="footer-email">{f.email}</a>
              </p>
            )}
            {f.address && (
              <p className="max-w-sm whitespace-pre-line text-zinc-400" data-testid="footer-address">
                {f.address}
              </p>
            )}
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Shop</p>
          <ul className="mt-4 space-y-2 text-sm text-zinc-300">
            {[
              ["Moissanite Watches", "/moissanite-watches"],
              ["Cuban Chains", "/cuban-chains"],
              ["Grillz", "/grillz"],
              ["Iced Bracelets", "/iced-bracelets"],
              ["Pendants", "/pendants"],
              ["Iced Out Glasses", "/moissanite-glasses-sunglasses"],
            ].map(([label, to]) => (
              <li key={to}>
                <Link to={to} className="transition-colors duration-200 hover:text-white hover:underline">{label}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Learn</p>
          <ul className="mt-4 space-y-2 text-sm text-zinc-300">
            <li><Link to="/blog" className="transition-colors duration-200 hover:text-white hover:underline">The Ice Report</Link></li>
            <li><Link to="/about" data-testid="footer-link-about" className="transition-colors duration-200 hover:text-white hover:underline">About Us</Link></li>
            <li><Link to="/contact" data-testid="footer-link-contact" className="transition-colors duration-200 hover:text-white hover:underline">Contact Us</Link></li>
            <li><Link to="/moissanite-watches-wholesale" data-testid="footer-link-wholesale" className="transition-colors duration-200 hover:text-white hover:underline">Wholesale &amp; B2B</Link></li>
            <li><Link to="/wishlist" className="transition-colors duration-200 hover:text-white hover:underline">Wishlist</Link></li>
            <li><Link to="/moissanite-cuban-chains-wholesale" data-testid="footer-link-cuban-wholesale" className="transition-colors duration-200 hover:text-white hover:underline">Cuban Chains Wholesale</Link></li>
            <li><Link to="/moissanite-teeth-grillz-wholesale" data-testid="footer-link-grillz-wholesale" className="transition-colors duration-200 hover:text-white hover:underline">Teeth Grillz Wholesale</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Info</p>
          <ul className="mt-4 space-y-2 text-sm text-zinc-300">
            <li><Link to="/shipping" data-testid="footer-link-shipping" className="transition-colors duration-200 hover:text-white hover:underline">Shipping Policy</Link></li>
            <li><Link to="/returns" data-testid="footer-link-returns" className="transition-colors duration-200 hover:text-white hover:underline">Returns & Refunds</Link></li>
            <li><Link to="/cancellation" data-testid="footer-link-cancellation" className="transition-colors duration-200 hover:text-white hover:underline">Cancellation Policy</Link></li>
            <li><Link to="/privacy" data-testid="footer-link-privacy" className="transition-colors duration-200 hover:text-white hover:underline">Privacy Policy</Link></li>
            <li><Link to="/terms" data-testid="footer-link-terms" className="transition-colors duration-200 hover:text-white hover:underline">Terms & Conditions</Link></li>
          </ul>
        </div>
      </div>

      {/* Sitemap ↔ Social row */}
      <div className="border-t border-white/15">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-5 sm:px-8 md:flex-row">
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[10px] uppercase tracking-[0.3em]">
            <Link to="/sitemap" data-testid="footer-sitemap-link" className="text-zinc-300 hover:text-white hover:underline">Sitemap</Link>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/sitemap.xml`}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-sitemap-xml-link"
              className="text-zinc-300 hover:text-white hover:underline"
            >
              sitemap.xml
            </a>
          </div>
          <SocialIcons social={social} />
        </div>
      </div>

      {/* Copyright ↔ Payment icons */}
      <div className="border-t border-white/15 bg-black">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-5 sm:px-8 md:flex-row">
          <p className="text-xs text-zinc-400" data-testid="footer-copyright">
            {copyrightRendered || `© ${new Date().getFullYear()} VVS Lab Jewelry. All Rights Reserved.`}
          </p>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">We Accept</span>
            <PaymentIcons />
          </div>
        </div>
      </div>
    </footer>
  );
};
